// Performance Monitoring Utilities
// Track and optimize profile system performance

import { useEffect } from 'react';

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.thresholds = {
      profileLoad: 2000, // 2 seconds
      imageLoad: 1000, // 1 second
      apiCall: 3000, // 3 seconds
      interaction: 100 // 100ms
    };
  }

  /**
   * Start timing an operation
   */
  startTimer(operation) {
    this.metrics.set(operation, {
      start: performance.now(),
      end: null,
      duration: null
    });
  }

  /**
   * End timing and record duration
   */
  endTimer(operation) {
    const metric = this.metrics.get(operation);
    if (!metric) return null;

    metric.end = performance.now();
    metric.duration = metric.end - metric.start;

    // Check if duration exceeds threshold
    const threshold = this.thresholds[operation.split(':')[0]];
    if (threshold && metric.duration > threshold) {
      console.warn(`Performance warning: ${operation} took ${metric.duration.toFixed(2)}ms (threshold: ${threshold}ms)`);
    }

    return metric.duration;
  }

  /**
   * Record a custom metric
   */
  recordMetric(name, value, unit = 'ms') {
    this.metrics.set(`custom:${name}`, {
      value,
      unit,
      timestamp: Date.now()
    });
  }

  /**
   * Get all metrics
   */
  getMetrics() {
    const results = {};
    
    for (const [key, value] of this.metrics.entries()) {
      results[key] = value;
    }
    
    return results;
  }

  /**
   * Get performance summary
   */
  getSummary() {
    const summary = {
      operations: {},
      slowOperations: [],
      averages: {}
    };

    // Group by operation type
    for (const [key, value] of this.metrics.entries()) {
      if (value.duration) {
        const type = key.split(':')[0];
        if (!summary.operations[type]) {
          summary.operations[type] = [];
        }
        summary.operations[type].push(value.duration);
        
        // Track slow operations
        const threshold = this.thresholds[type];
        if (threshold && value.duration > threshold) {
          summary.slowOperations.push({
            operation: key,
            duration: value.duration,
            threshold
          });
        }
      }
    }

    // Calculate averages
    for (const [type, durations] of Object.entries(summary.operations)) {
      if (durations.length > 0) {
        summary.averages[type] = {
          avg: durations.reduce((a, b) => a + b, 0) / durations.length,
          min: Math.min(...durations),
          max: Math.max(...durations),
          count: durations.length
        };
      }
    }

    return summary;
  }

  /**
   * Clear all metrics
   */
  clear() {
    this.metrics.clear();
  }

  /**
   * Export metrics for analysis
   */
  export() {
    return {
      metrics: Object.fromEntries(this.metrics),
      summary: this.getSummary(),
      timestamp: new Date().toISOString()
    };
  }
}

// Bundle size analyzer
export const bundleAnalyzer = {
  /**
   * Analyze component bundle sizes
   */
  analyzeComponent(componentName, component) {
    try {
      const size = new Blob([component.toString()]).size;
      console.log(`Component ${componentName} size: ${(size / 1024).toFixed(2)}KB`);
      return size;
    } catch (error) {
      console.error('Failed to analyze component:', error);
      return 0;
    }
  },

  /**
   * Track lazy loaded chunks
   */
  trackChunkLoad(chunkName, startTime) {
    const duration = performance.now() - startTime;
    console.log(`Chunk ${chunkName} loaded in ${duration.toFixed(2)}ms`);
    return duration;
  }
};

// Image optimization utilities
export const imageOptimizer = {
  /**
   * Check if image needs optimization
   */
  needsOptimization(file) {
    const MAX_SIZE = 2 * 1024 * 1024; // 2MB
    const MAX_DIMENSION = 1920;
    
    return new Promise((resolve) => {
      if (file.size > MAX_SIZE) {
        resolve({ needs: true, reason: 'size' });
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
          resolve({ needs: true, reason: 'dimensions' });
        } else {
          resolve({ needs: false });
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ needs: false });
      };
      
      img.src = url;
    });
  },

  /**
   * Get optimization suggestions
   */
  getOptimizationSuggestions(images) {
    const suggestions = [];
    let totalSize = 0;
    let oversizedCount = 0;

    images.forEach(img => {
      totalSize += img.size || 0;
      if (img.size > 1024 * 1024) { // 1MB
        oversizedCount++;
      }
    });

    if (oversizedCount > 0) {
      suggestions.push({
        type: 'size',
        message: `${oversizedCount} images are over 1MB and could be compressed`,
        impact: 'high'
      });
    }

    if (totalSize > 10 * 1024 * 1024) { // 10MB total
      suggestions.push({
        type: 'total',
        message: `Total image size is ${(totalSize / (1024 * 1024)).toFixed(1)}MB`,
        impact: 'medium'
      });
    }

    return suggestions;
  }
};

// Network performance utilities
export const networkMonitor = {
  /**
   * Check connection quality
   */
  getConnectionQuality() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (!connection) {
      return 'unknown';
    }

    const effectiveType = connection.effectiveType;
    const downlink = connection.downlink;

    if (effectiveType === '4g' && downlink > 5) {
      return 'excellent';
    } else if (effectiveType === '4g' || effectiveType === '3g') {
      return 'good';
    } else {
      return 'poor';
    }
  },

  /**
   * Get adaptive quality settings
   */
  getAdaptiveSettings() {
    const quality = this.getConnectionQuality();
    
    const settings = {
      excellent: {
        imageQuality: 'high',
        preloadImages: true,
        autoplayVideos: true,
        chunkSize: 'large'
      },
      good: {
        imageQuality: 'medium',
        preloadImages: true,
        autoplayVideos: false,
        chunkSize: 'medium'
      },
      poor: {
        imageQuality: 'low',
        preloadImages: false,
        autoplayVideos: false,
        chunkSize: 'small'
      },
      unknown: {
        imageQuality: 'medium',
        preloadImages: true,
        autoplayVideos: false,
        chunkSize: 'medium'
      }
    };

    return settings[quality];
  }
};

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Performance React Hook
export const usePerformanceTracking = (componentName) => {
  useEffect(() => {
    performanceMonitor.startTimer(`component:${componentName}`);
    
    return () => {
      const duration = performanceMonitor.endTimer(`component:${componentName}`);
      if (duration && duration > 1000) {
        console.warn(`Component ${componentName} took ${duration.toFixed(2)}ms to mount`);
      }
    };
  }, [componentName]);
};
